import { API_CONFIG } from '../config/api.config';

/**
 * @typedef {Object} SendOwnerNotificationParams
 * @property {string} ownerPhone
 * @property {string} [ownerName]
 * @property {string} [buyerName]
 * @property {string} buyerPhone
 * @property {string} [propertyTitle]
 * @property {string} [propertyLocation]
 */

/** Digits only, for India include leading 91 when missing (10-digit local). */
function normalizeSmsDestination(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 10) {
    return `91${digits}`;
  }
  return digits;
}

/** GSM-friendly plain text: DLT / carriers often reject odd Unicode even when API returns 000. */
function sanitizeSmsPlainText(s) {
  return String(s || '')
    .replace(/[\u060C\u066B،]/g, ',')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Message body must match Infinitio DLT template exactly (delivery report `Rejected(666)` = mismatch).
 * Provider sample: `Hi test, a user with contact number 9191919191 has viewed your contact on 360Coordinates for test VEDANTINFO`
 * @param {SendOwnerNotificationParams} params
 */
function buildOwnerViewSmsText(params) {
  const { ownerName, buyerPhone, propertyTitle, propertyLocation } = params;
  const name = sanitizeSmsPlainText((ownerName || 'User').trim()).slice(0, 80);
  const buyer = normalizeSmsDestination(buyerPhone);
  const title = sanitizeSmsPlainText((propertyTitle || 'Property').trim()).slice(0, 120);
  const loc = sanitizeSmsPlainText((propertyLocation || '').trim()).slice(0, 80);
  const tail = loc ? `${title} ${loc}` : title;
  const suffix = sanitizeSmsPlainText(API_CONFIG.INFINITIO_SMS_DLT_MESSAGE_SUFFIX || '').trim();
  const msg = suffix.length
    ? `Hi ${name}, a user with contact number ${buyer} has viewed your contact on 360Coordinates for ${tail} ${suffix}`
    : `Hi ${name}, a user with contact number ${buyer} has viewed your contact on 360Coordinates for ${tail}`;
  return msg.slice(0, 480);
}

/**
 * Send SMS to property owner when buyer views contact (Show Owner Details).
 * Infinitio `SendSMS` rejects GET; use POST with query string + empty body (native `fetch`, no axios).
 * @param {SendOwnerNotificationParams} params
 * @returns {Promise<boolean>}
 */
export async function sendOwnerViewNotification(params) {
  console.log('[SMS Service] ========== START SMS (Infinitio) ==========');

  try {
    if (!API_CONFIG.INFINITIO_SMS_API_KEY) {
      console.warn(
        '[SMS Service] INFINITIO_SMS_API_KEY is empty — add REACT_APP_INFINITIO_SMS_API_KEY to `.env` and restart the dev server.',
      );
      return false;
    }

    const ownerMsisdn = normalizeSmsDestination(params.ownerPhone);
    if (!ownerMsisdn) {
      console.warn('[SMS Service] Owner phone missing or invalid — skipping SMS.');
      return false;
    }

    const text = buildOwnerViewSmsText(params);
    console.log('[SMS Service] SMS length (chars):', text.length);

    const templateId = String(API_CONFIG.INFINITIO_SMS_TEMPLATE_ID ?? '').trim();
    const query = new URLSearchParams({
      APIkey: API_CONFIG.INFINITIO_SMS_API_KEY,
      senderid: API_CONFIG.INFINITIO_SMS_SENDER_ID,
      channel: API_CONFIG.INFINITIO_SMS_CHANNEL,
      DCS: API_CONFIG.INFINITIO_SMS_DCS,
      flashsms: API_CONFIG.INFINITIO_SMS_FLASHSMS,
      number: ownerMsisdn,
      text,
      route: API_CONFIG.INFINITIO_SMS_ROUTE,
    });
    if (templateId) {
      query.set('templateid', templateId);
    }

    const url = `${API_CONFIG.INFINITIO_SMS_SEND_URL}?${query.toString()}`;
    console.log(
      '[SMS Service] fetch POST SendSMS:',
      API_CONFIG.INFINITIO_SMS_SEND_URL,
      '| mode:',
      templateId ? `with templateid (${templateId})` : 'without templateid',
      '| number:',
      ownerMsisdn,
    );

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: '',
      redirect: 'manual',
    });

    const responseText = await response.text();
    console.log('[SMS Service] HTTP', response.status, responseText?.slice(0, 500));

    if (response.status < 200 || response.status >= 300) {
      return false;
    }

    const trimmed = responseText.trim();
    if (trimmed.includes("does not support http method 'GET'")) {
      console.warn(
        "[SMS Service] Server responded as if the request was GET. That only happens for GET; the app uses POST. " +
          'If you opened the API URL in a browser or Postman with GET, use POST instead.',
      );
      return false;
    }
    if (!trimmed) {
      return true;
    }

    try {
      const data = JSON.parse(trimmed);
      if (typeof data.success === 'boolean') {
        return data.success;
      }
      const ec = String(data.ErrorCode ?? '').trim();
      const okCodes = ['0', '00', '000'];
      if (okCodes.includes(ec)) {
        return true;
      }
      const em = String(data.ErrorMessage ?? '').toLowerCase();
      if (em === 'success' && (ec === '' || okCodes.includes(ec))) {
        return true;
      }
      if (data.ErrorCode !== undefined && ec !== '' && !okCodes.includes(ec)) {
        return false;
      }
    } catch {
      const lower = trimmed.toLowerCase();
      if (lower.includes('fail') || lower.includes('error') || lower.includes('invalid')) {
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('[SMS Service] Exception:', error?.message || error);
    return false;
  } finally {
    console.log('[SMS Service] ========== END SMS (Infinitio) ==========');
  }
}

/**
 * @param {SendOwnerNotificationParams} params
 * @returns {Promise<boolean>}
 */
export async function sendOwnerViewNotificationMock(params) {
  console.log('[MOCK SMS] Would send to owner:', params.ownerPhone, buildOwnerViewSmsText(params));
  await new Promise((r) => setTimeout(r, 300));
  return true;
}
